import React, { useState, useEffect } from 'react';
import {
  ExternalLink,
  Target,
  BarChart3,
  Wallet,
  Blocks,
  Scale,
  Settings,
  TrendingUp,
  Users,
  ArrowRight,
  Quote,
  Star,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = {
  Target,
  BarChart3,
  Wallet,
  Blocks,
  Scale,
  Settings,
  TrendingUp,
  Users,
};

/**
 * CustomPanel renders a cloned section on the main site.
 * It fetches items from /api/content/custom-panel/{panel.id}
 * and renders them using the layout of panel.type.
 */
const CustomPanel = ({ panel }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedFaq, setExpandedFaq] = useState(null);

  useEffect(() => {
    const fetchItems = async () => {
      try {
        const res = await axios.get(`${API}/content/custom-panel/${panel.id}`);
        const data = Array.isArray(res.data) ? res.data : [];
        setItems(data.filter((item) => item.visible !== false));
      } catch (error) {
        console.log(`Custom panel ${panel.id}: no items`);
        setItems([]);
      } finally {
        setLoading(false);
      }
    };
    fetchItems();
  }, [panel.id]);

  if (loading) return null;
  if (items.length === 0) return null;

  const type = panel.type || panel.id;
  const sectionId = panel.id;
  const title = panel.name || 'Custom Section';

  // Background alternation helper — light then dark to break monotony
  const bgClass = panel.bgClass || 'bg-white';

  return (
    <section
      id={sectionId}
      className={`py-8 ${bgClass}`}
      data-testid={`custom-panel-${sectionId}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-10">
          <h2 className="text-2xl sm:text-3xl font-serif font-bold text-navy-900 mb-4">
            {title}
          </h2>
        </div>

        {/* Render items based on type */}
        {type === 'investments' && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {items
              .sort((a, b) => (a.order || 0) - (b.order || 0))
              .map((item) => (
                <div
                  key={item.id}
                  className="panel-flair group bg-white rounded-2xl overflow-hidden border-2 border-slate-200/70 hover:border-gold-400 transition-all duration-500 hover:-translate-y-1 flex flex-col h-full"
                >
                  <div className="h-48 bg-white flex items-center justify-center p-6 relative overflow-hidden">
                    {item.logo ? (
                      <img
                        src={item.logo}
                        alt={item.name}
                        className="max-h-32 max-w-full object-contain group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-24 h-24 bg-navy-900/10 rounded-xl flex items-center justify-center">
                        <span className="text-2xl font-bold text-navy-900">
                          {item.name?.charAt(0) || 'I'}
                        </span>
                      </div>
                    )}
                    {item.category && (
                      <div className="absolute top-4 right-4">
                        <span className="inline-block px-3 py-1 bg-navy-900/10 text-navy-800 rounded-full text-xs font-medium">
                          {item.category}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <h3 className="text-xl font-bold text-navy-900 mb-3 group-hover:text-gold-600 transition-colors">
                      {item.name}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed mb-4">
                      {item.description}
                    </p>
                    {item.additional_info && (
                      <p className="text-gray-500 text-sm italic mb-4">
                        {item.additional_info}
                      </p>
                    )}
                    {item.website_url && (
                      <a
                        href={item.website_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-auto pt-3 inline-flex items-center text-gold-600 font-semibold text-sm hover:text-gold-700 transition-colors"
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        {item.website || item.website_url}
                      </a>
                    )}
                  </div>
                </div>
              ))}
          </div>
        )}

        {type === 'services' && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {items
              .sort((a, b) => (a.order || 0) - (b.order || 0))
              .map((service) => {
                const IconComponent = iconMap[service.icon] || Target;
                return (
                  <div
                    key={service.id}
                    className="panel-flair group bg-white rounded-xl overflow-hidden border-2 border-slate-200/70 hover:border-gold-400 transition-all duration-300 hover:-translate-y-1 flex flex-col h-full"
                  >
                    <div className="px-5 pt-5">
                      <div className="w-12 h-12 bg-gradient-to-br from-navy-800 to-navy-900 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                        <IconComponent className="w-6 h-6 text-gold-400" />
                      </div>
                    </div>
                    <div className="p-5 flex flex-col flex-1">
                      <h3 className="text-lg font-bold text-navy-900 mb-2 group-hover:text-gold-600 transition-colors">
                        {service.title}
                      </h3>
                      <p className="text-gray-600 text-sm leading-snug mb-3">
                        {service.description}
                      </p>
                      <a
                        href="#contact"
                        className="mt-auto pt-3 inline-flex items-center text-gold-600 font-semibold text-sm hover:text-gold-700 transition-colors"
                      >
                        <ArrowRight className="w-3.5 h-3.5 mr-1.5" />
                        Learn More
                      </a>
                    </div>
                  </div>
                );
              })}
          </div>
        )}

        {type === 'testimonials' && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {items
              .sort((a, b) => (a.order || 0) - (b.order || 0))
              .map((t) => (
                <div
                  key={t.id}
                  className="panel-flair bg-white rounded-2xl p-8 border-2 border-slate-200/70 hover:border-gold-400 overflow-hidden transition-all"
                >
                  <Quote className="w-10 h-10 text-gold-400 mb-4" />
                  <div className="flex gap-1 mb-4">
                    {Array.from({ length: t.rating || 5 }).map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-gold-500 text-gold-500" />
                    ))}
                  </div>
                  <p className="text-gray-700 italic leading-relaxed mb-6">
                    "{t.quote}"
                  </p>
                  <div>
                    <p className="font-semibold text-navy-900">{t.author}</p>
                    {t.company && (
                      <p className="text-sm text-gray-500">{t.company}</p>
                    )}
                  </div>
                </div>
              ))}
          </div>
        )}

        {type === 'case-studies' && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {items
              .sort((a, b) => (a.order || 0) - (b.order || 0))
              .map((cs) => (
                <div
                  key={cs.id}
                  className="panel-flair bg-white rounded-2xl overflow-hidden border-2 border-slate-200/70 hover:border-gold-400 transition-all"
                >
                  {cs.image && (
                    <div className="h-48 overflow-hidden">
                      <img
                        src={cs.image}
                        alt={cs.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <div className="p-6">
                    {cs.industry && (
                      <span className="inline-block px-3 py-1 bg-gold-100 text-gold-700 rounded-full text-xs font-medium mb-3">
                        {cs.industry}
                      </span>
                    )}
                    <h3 className="text-xl font-bold text-navy-900 mb-4">
                      {cs.title}
                    </h3>
                    <div className="space-y-3 text-sm">
                      {cs.challenge && (
                        <div>
                          <span className="font-semibold text-navy-900">Challenge: </span>
                          <span className="text-gray-600">{cs.challenge}</span>
                        </div>
                      )}
                      {cs.solution && (
                        <div>
                          <span className="font-semibold text-navy-900">Solution: </span>
                          <span className="text-gray-600">{cs.solution}</span>
                        </div>
                      )}
                      {cs.result && (
                        <div>
                          <span className="font-semibold text-gold-600">Result: </span>
                          <span className="text-gray-700">{cs.result}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
          </div>
        )}

        {type === 'faq' && (
          <div className="max-w-3xl mx-auto space-y-4">
            {items
              .sort((a, b) => (a.order || 0) - (b.order || 0))
              .map((f) => {
                const isOpen = expandedFaq === f.id;
                return (
                  <div
                    key={f.id}
                    className="panel-flair bg-white rounded-xl border-2 border-slate-200/70 hover:border-gold-400 overflow-hidden transition-all"
                  >
                    <button
                      onClick={() => setExpandedFaq(isOpen ? null : f.id)}
                      className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors"
                      data-testid={`custom-faq-toggle-${f.id}`}
                    >
                      <span className="font-semibold text-navy-900">{f.question}</span>
                      {isOpen ? (
                        <ChevronUp className="w-5 h-5 text-gold-600 flex-shrink-0" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gold-600 flex-shrink-0" />
                      )}
                    </button>
                    {isOpen && (
                      <div className="px-5 pb-5 text-gray-600 leading-relaxed">
                        {f.answer}
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        )}

        {type === 'insights' && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {items
              .sort((a, b) => (a.order || 0) - (b.order || 0))
              .map((post) => (
                <article
                  key={post.id}
                  className="panel-flair bg-white rounded-2xl overflow-hidden border-2 border-slate-200/70 hover:border-gold-400 transition-all"
                >
                  {post.image && (
                    <div className="h-48 overflow-hidden">
                      <img
                        src={post.image}
                        alt={post.title}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-3 text-xs">
                      {post.category && (
                        <span className="px-3 py-1 bg-gold-100 text-gold-700 rounded-full font-medium">
                          {post.category}
                        </span>
                      )}
                      {post.date && (
                        <span className="text-gray-500">{post.date}</span>
                      )}
                    </div>
                    <h3 className="text-xl font-bold text-navy-900 mb-3">
                      {post.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {post.excerpt}
                    </p>
                  </div>
                </article>
              ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default CustomPanel;
